// Weekly completion data: completed tasks grouped per day + uren per project.
import { useMemo } from 'react';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import {
  buildTasksMap,
  filterDayWorkUnits,
  getDayCapacitySnapshot,
} from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
import { isOnOrAfterHistoryCutoff } from '@/features/dashboard/utils/dashboardHistoryLimit';
import {
  WEEKLY_DAY_RANGE,
  VISIBLE_BARS_DESKTOP,
  toDateStr,
  buildDayRange,
  formatDayLabel,
  formatBarDay,
  getMaxWeekOffset,
  normalizeScheduleDate,
  NO_PROJECT_KEY,
  buildProjectHours,
} from '@/features/dashboard/hooks/weeklyCompletionDateUtils';

export { getMaxWeekOffset } from '@/features/dashboard/hooks/weeklyCompletionDateUtils';

/**
 * Get visible bar count based on breakpoint.
 * Phase 5: bars vary by device size.
 * @param {string} breakpoint - from getBreakpoint() or useBreakpoint()
 * @returns {number} - 3 for phone, 5 for tablet, 8 for desktop
 */
export const getVisibleBarDays = (breakpoint) => {
  if (breakpoint === 'xs' || breakpoint === 's') return 3; // phone
  if (breakpoint === 'm' || breakpoint === 'l') return 5; // tablet
  return VISIBLE_BARS_DESKTOP; // desktop (xl)
};

/**
 * @param {number} [weekOffset]
 * @param {{ historyCutoffDateStr?: string | null }} [options]
 */
export const useWeeklyCompletion = (weekOffset = 0, options = {}) => {
  const { historyCutoffDateStr = null } = options;
  const { allTasks, projectDisplayMap, projectColorMap, tasksMap } =
    useTasksContext();

  return useMemo(() => {
    const map = tasksMap?.size ? tasksMap : buildTasksMap(allTasks ?? []);
    const now = new Date();
    const todayStr = toDateStr(now);
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = toDateStr(yesterday);
    const normalizedWeekOffset = Math.max(0, weekOffset);

    const rangeEnd = new Date(now);
    rangeEnd.setDate(
      rangeEnd.getDate() - normalizedWeekOffset * WEEKLY_DAY_RANGE,
    );
    const isCurrentPeriod = normalizedWeekOffset === 0;
    const dayRange = buildDayRange(WEEKLY_DAY_RANGE, rangeEnd);

    const allByDay = new Map();
    let earliestCompletedStr = null;

    if (allTasks && allTasks.length > 0) {
      allTasks.forEach((task) => {
        if (!task.date) return;
        const dayStr = normalizeScheduleDate(task.date);
        if (!dayStr) return;

        if (task.completed) {
          if (!earliestCompletedStr || dayStr < earliestCompletedStr) {
            earliestCompletedStr = dayStr;
          }
        }

        if (!isOnOrAfterHistoryCutoff(dayStr, historyCutoffDateStr)) return;

        if (!allByDay.has(dayStr)) allByDay.set(dayStr, []);
        allByDay.get(dayStr).push(task);
      });
    }

    const barData = [];
    const completedDays = [];

    dayRange.forEach(({ dateStr, date }) => {
      if (!isOnOrAfterHistoryCutoff(dateStr, historyCutoffDateStr)) return;

      const dayAllTasksRaw = allByDay.get(dateStr) || [];
      const dayCompletedTasksRaw = dayAllTasksRaw.filter(
        (task) => task.completed,
      );
      const dayCompletedTasks = filterDayWorkUnits(dayCompletedTasksRaw, map);
      const { projectHours, totalHours } = buildProjectHours(
        dayCompletedTasks,
        dayCompletedTasksRaw,
        map,
        projectDisplayMap,
      );

      const planned =
        isCurrentPeriod && dateStr === todayStr
          ? getDayCapacitySnapshot(dayAllTasksRaw, map).plannedMins / 60
          : null;

      const projects = Object.entries(projectHours).map(
        ([projectId, { name, hours }]) => ({
          projectId,
          name,
          hours: Math.round(hours * 100) / 100,
          colorIndex:
            projectId === NO_PROJECT_KEY
              ? undefined
              : projectColorMap?.get(projectId),
        }),
      );

      barData.push({
        dateStr,
        day: formatBarDay(date, dateStr, todayStr),
        hours: Math.round(totalHours * 100) / 100,
        planned:
          planned !== null && planned !== undefined
            ? Math.round(planned * 100) / 100
            : null,
        projects,
      });

      if (dayCompletedTasks.length > 0) {
        completedDays.push({
          dateStr,
          label: formatDayLabel(date, dateStr, todayStr, yesterdayStr),
          count: dayCompletedTasks.length,
          tasks: dayCompletedTasks,
        });
      }
    });

    const canGoBack = historyCutoffDateStr
      ? Boolean(
          earliestCompletedStr &&
            earliestCompletedStr < historyCutoffDateStr,
        )
      : normalizedWeekOffset <
        getMaxWeekOffset(earliestCompletedStr, todayStr);
    const canGoForward = historyCutoffDateStr
      ? false
      : normalizedWeekOffset > 0;

    return {
      barData,
      completedDays: completedDays.reverse(),
      canGoBack,
      canGoForward,
    };
  }, [
    allTasks,
    projectDisplayMap,
    projectColorMap,
    tasksMap,
    weekOffset,
    historyCutoffDateStr,
  ]);
};
